import { Button, Dropdown, Input } from '../../../components';
import React, { useEffect, useRef } from 'react';
import { usePaymentReminder } from '@/context/paymentReminderContext/PaymentReminderContextProvider';
import MailBody from './MailBody';

const MailSender = ({ closeModal }) => {
  const {
    senderEmail = [],
    paymentReminderSenderEmailAPIId,
    emailTemplate = {},
    setEmailTemplate,
    selectedFrom,
    setSelectedFrom,
    selectedReminder,
    setSelectedReminder,
    selectedSubject,
    setSelectedSubject,
    selectedBody,
    setSelectedBody,
    updateEmailTemplateAPI,
    setEmailSenderAndTemplate,
  } = usePaymentReminder();

  // Ensure senderEmail is an array before mapping
  const fromOptions = Array.isArray(senderEmail)
    ? senderEmail.map((email) => ({
        label: `${email.description} <${email.email}>`,
        value: email.id,
        data: email,
      }))
    : [];

  const reminderOptions = [
    { label: 'First Reminder', value: 1 },
    { label: 'Second Reminder', value: 2 },
    { label: 'Final Reminder', value: 3 },
  ];

  const handleFromChange = (e) => {
    const selectedId = e.target.value;
    setSelectedFrom(selectedId); // Persist "From" in context
    paymentReminderSenderEmailAPIId(selectedId);
  };

  const handleReminderChange = (e) => {
    const selectedId = parseInt(e.target.value, 10);
    setSelectedReminder(selectedId); // Persist "Reminder" in context

    const selectedTemplate = Array.isArray(emailTemplate.emailTemplates)
      ? emailTemplate.emailTemplates.find(
          (template) => template.templateId === selectedId,
        )
      : null;
    if (selectedTemplate) {
      setSelectedSubject(selectedTemplate.subject); // Persist "Subject" in context
      setSelectedBody(selectedTemplate.body); // Persist "Body" in context
    }
  };

  const emailData = {
    from: senderEmail[0]?.email || '',
    subject: selectedSubject, // Use context value
    body: selectedBody, // Use context value
  };

  // Track if it's the first load
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (isFirstLoad.current) {
      setSelectedFrom('');
      setSelectedReminder('');
      setSelectedSubject('');
      setSelectedBody('');
      setEmailTemplate(null);
      isFirstLoad.current = false;
    }
    // eslint-disable-next-line
  }, []);

  const updateEmailTemplate = () => {
    const selectedTemplate = emailTemplate.emailTemplates.find(
      (template) => template.templateId === selectedReminder,
    );

    if (selectedTemplate) {
      updateEmailTemplateAPI(selectedTemplate.id, {
        templateId: selectedReminder,
        header: selectedTemplate.header,
        subject: selectedSubject,
        body: selectedBody,
        from: selectedFrom,
        contactPerson: selectedTemplate.contactPerson,
      });
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div>
        <Dropdown
          label="From"
          name="from"
          options={fromOptions}
          value={selectedFrom} // <-- Control value from context
          onChange={handleFromChange} // Handle dropdown change for "From"
          required
        />
      </div>
      <div>
        <Dropdown
          label="Reminder"
          name="reminder"
          options={reminderOptions}
          value={selectedReminder} // <-- Control value from context
          onChange={handleReminderChange} // Handle dropdown change for "Reminder"
          required
        />
      </div>
      <div>
        <Input
          label="Subject"
          name="subject"
          disabled
          value={selectedSubject} // Use context value
          onBlur={updateEmailTemplate} // Update email template on blur
          onChange={(e) => {
            setSelectedSubject(e.target.value); // Persist "Subject" in context
            setEmailTemplate((prev) => ({
              ...prev,
              emailTemplates: prev.emailTemplates.map((template) =>
                template.templateId === selectedReminder
                  ? { ...template, subject: e.target.value }
                  : template,
              ),
            }));
          }}
          required
        />
      </div>
      <MailBody
        emailData={emailData}
        onBlur={updateEmailTemplate} // Update email template on blur
        onBodyChange={(updatedBody) => {
          setSelectedBody(updatedBody); // Persist "Body" in context
          setEmailTemplate((prev) => ({
            ...prev,
            emailTemplates: prev.emailTemplates.map((template) =>
              template.templateId === selectedReminder
                ? { ...template, body: updatedBody }
                : template,
            ),
          }));
        }}
      />
      <div className="flex justify-end mt-4">
        <Button
          onClick={() => {
            setEmailSenderAndTemplate(
              selectedFrom,
              selectedReminder,
              closeModal,
            );
          }}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
          disabled={!selectedSubject || !emailData.from}>
          Select Template
        </Button>
      </div>
    </div>
  );
};

export default MailSender;
